/* eslint-disable camelcase */
import Config from 'app.config';
import dayjs from 'dayjs';
import { omitBy, sortBy, isEmpty } from 'lodash-es';
import { call, put, select, takeLatest, race, delay } from 'redux-saga/effects';
import { yieldErrorHandling } from 'utils/common';
import request, { buildRequestObj, getAuthToken } from 'utils/request';
import { buildConfirmAttributionPayload } from '../../utils/booking-attribution';
import { loadState } from '../../utils/localStorage_persist';
import { selectAppointmentDomain } from './selectors';
import {
  buildCouponValidateRequest,
  couponValidateResultMatchesField,
  wrongFieldMessageForCodeKind,
} from '../../utils/checkout-coupon-codes';

import {
  APPLY_REFERRAL,
  CANCEL_USER_APPOINTMENT,
  CHECK_GIFT_CODE,
  CREATE_USER_APPOINTMENT,
  FETCH_APPOINTMENTS,
  FETCH_AVAILABLE_THERAPISTS,
  FETCH_LOCATIONS,
  FETCH_LOCATION_MORE_INFO,
  FETCH_LOCATION_SERVICES,
  FETCH_MASSAGE_TYPES,
  FETCH_NEAREST_LOCATION,
  FETCH_SERVICE_ADDONS,
  FETCH_STATUS,
  FETCH_USER_COMPLETED_APPOINTMENTS,
  FETCH_USER_PRESSURE,
  FETCH_USER_SERVICE_ADDONS,
  GET_IP_ADDRESS,
  GET_LIABILITY_WAIVER,
  RESERVE_APPOINTMENT_SAGA,
  RETRY_APPOINTMENT_CHARGE,
  SEND_REQUIRED_GOVT_INFO,
  SEND_SIGNATURE,
  SUGGESTED_TIP_AMOUNT,
  UPDATE_APPOINTMENT_TEXT,
  UPDATE_USER_PRESSURE,
  UPDATE_USER_SERVICE_ADDONS,
} from './constants';

import {
  applyReferralSuccess,
  cancelUserAppointmentFailure,
  cancelUserAppointmentSuccess,
  checkGiftCodeFailure,
  checkGiftCodeSuccess,
  createUserAppointmentFailure,
  createUserAppointmentSuccess,
  fetchAppointmentsFailure,
  fetchAppointmentsSuccess,
  fetchAvailableTherapistsFailure,
  fetchAvailableTherapistsSuccess,
  fetchLocationMoreInfoFailure,
  fetchLocationMoreInfoSuccess,
  fetchLocationServicesFailure,
  fetchLocationServicesSuccess,
  fetchLocationsFailure,
  fetchLocationsSuccess,
  fetchMassageTypesFailure,
  fetchMassageTypesSuccess,
  fetchNearestLocationFailure,
  fetchNearestLocationSuccess,
  fetchServicesAddonsFailure,
  fetchServicesAddonsSuccess,
  fetchStatusFailure,
  fetchStatusSuccess,
  fetchUserCompletedAppointmentsFailure,
  fetchUserCompletedAppointmentsSuccess,
  fetchUserPressureFailure,
  fetchUserPressureSuccess,
  fetchUserServiceAddonsFailure,
  fetchUserServiceAddonsSuccess,
  getIpAddressFailure,
  getIpAddressSuccess,
  getLiabilityWaiverFailure,
  getLiabilityWaiverSuccess,
  getSuggestedTipAmountSuccess,
  retryAppointmentChargeFailure,
  retryAppointmentChargeSuccess,
  sendRequiredGovtInfoFailure,
  sendRequiredGovtInfoSuccess,
  sendSignatureFailure,
  sendSignatureSuccess,
  setNotAvailableTillDate,
  updateAppointmentTextFailure,
  updateAppointmentTextSuccess,
  updateUserPressureFailure,
  updateUserPressureSuccess,
  updateUserServiceAddonsFailure,
  updateUserServiceAddonsSuccess,
} from './actions';

export function* fetchLocationsSaga() {
  const reqObj = buildRequestObj('GET', {}, false);
  const reqUrl = `${Config.apiBasev2}/locations`;
  // const reqUrl = `https://api.squeezemassage.com/api/spa/locations`;

  try {
    const result = yield call(request, reqUrl, reqObj);
    // Filter out Somerville and Watertown locations (case-insensitive)
    const filteredItems = (result.items || []).filter(loc => {
      const urlName = loc && loc.url_name && String(loc.url_name).toLowerCase();
      return Boolean(
        urlName && urlName !== 'somerville' && urlName !== 'watertown',
      );
    });
    yield put(fetchLocationsSuccess(filteredItems));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchLocationsFailure,
      'locations fetch failed',
    );
  }
}

function* watchFetchLocations() {
  yield takeLatest(FETCH_LOCATIONS, fetchLocationsSaga);
}

export function* fetchLocationServicesSaga(action) {
  const { locationId, isAuth } = action;
  const isAuthNeeded = !!isAuth;
  // Keep impersonation token format identical to existing auth calls.
  let impersonateToken;
  if (
    isAuthNeeded &&
    typeof window !== 'undefined' &&
    window.localStorage.getItem('impersonateUser') === 'true'
  ) {
    const token = getAuthToken();
    impersonateToken = token ? String(token).trim() : undefined;
  } else {
    impersonateToken = undefined;
  }
  const reqObj = buildRequestObj(
    'GET',
    {},
    isAuthNeeded,
    impersonateToken || undefined,
  );
  const reqUrl = `${Config.apiBasev2}/location/${locationId}/services`;
  // const reqUrl = "https://api-qa.squeezemassage.com/api/spa/location/1/services";
  try {
    const result = yield call(request, reqUrl, reqObj);
    result.services = result.services.filter(
      item => item.label !== 'Mini Squeeze',
    );
    yield put(fetchLocationServicesSuccess(result));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchLocationServicesFailure,
      'location services fetch failed',
    );
  }
}

function* watchFetchLocationServices() {
  yield takeLatest(FETCH_LOCATION_SERVICES, fetchLocationServicesSaga);
}

export function* fetchServicesAddons(action) {
  const { locationId } = action;

  const reqObj = buildRequestObj('GET', {}, false);

  const reqUrl = `${Config.apiBasev2}/location/${locationId}/addon-service`;
  console.log('Making API request to:', reqUrl);

  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(fetchServicesAddonsSuccess(result && result.items, locationId));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchServicesAddonsFailure,
      'location services fetch failed',
    );
  }
}

function* watchFetchServicesAddons() {
  yield takeLatest(FETCH_SERVICE_ADDONS, fetchServicesAddons);
}

export function* fetchUserServiceAddonsSaga() {
  const reqObj = buildRequestObj('GET', {}, true);
  const reqUrl = `${Config.apiBasev2}/user/addons`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(fetchUserServiceAddonsSuccess(result.items));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchUserServiceAddonsFailure,
      'Could not fetch User Addons',
    );
  }
}
function* watchFetchUserServiceAddons() {
  yield takeLatest(FETCH_USER_SERVICE_ADDONS, fetchUserServiceAddonsSaga);
}

export function* updateUserServiceAddonsSaga(action) {
  // eslint-disable-next-line
  const { addons } = action;  
  const reqObj = buildRequestObj('POST', { addons }, true);
  const reqUrl = `${Config.apiBasev2}/user/addons`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(updateUserServiceAddonsSuccess(result.items));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      updateUserServiceAddonsFailure,
      'Could not Update User Addons',
    );
  }
}

function* watchUpdateUserServiceAddonsSaga() {
  yield takeLatest(UPDATE_USER_SERVICE_ADDONS, updateUserServiceAddonsSaga);
}

export function* fetchLocationMoreInfoSaga(action) {
  const reqObj = buildRequestObj('GET', {}, false);
  const { locationId } = action;
  const reqUrl = `${Config.apiBasev2}/location/${locationId}`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(fetchLocationMoreInfoSuccess(result));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchLocationMoreInfoFailure,
      'location details fetch failed',
    );
  }
}

function* watchFetchLocationMoreInfo() {
  yield takeLatest(FETCH_LOCATION_MORE_INFO, fetchLocationMoreInfoSaga);
}

export function* fetchMassageTypesSaga() {
  const reqObj = buildRequestObj('GET', {}, false);
  const reqUrl = `${Config.apiBasev2}/massage_types`;

  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(fetchMassageTypesSuccess(result.items));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchMassageTypesFailure,
      'massage types fetch failed',
    );
  }
}

function* watchFetchMassageTypes() {
  yield takeLatest(FETCH_MASSAGE_TYPES, fetchMassageTypesSaga);
}

export function* fetchAvailableTherapistsSaga(action) {
  const {
    /* eslint-disable */
    start_date_time,
    end_date_time,
    location_id,
    massage_type_id,
    pregnancy,
    therapist_id,
    service_id,
    nextAvailability,
    pre_live,
    addon_type_ids,
  } = action;

  const isPreLive = pre_live ? `&pre_live=${pre_live}` : '';
  const reqObj = buildRequestObj('GET', {}, true);
  const params = `location_id=${location_id}&service_id=${service_id ||
    1}&start_date_time=${start_date_time}&end_date_time=${end_date_time}${isPreLive}`;
  let reqUrl = nextAvailability
    ? `${Config.apiBasev2}/therapists/availability?${params}&future=true`
    : `${Config.apiBasev2}/therapists/availability?${params}`;
  if (pregnancy) {
    reqUrl = reqUrl.concat(`&pregnancy=${pregnancy}`);
  }
  if (therapist_id && therapist_id.length) {
    reqUrl = reqUrl.concat(`&therapist_id=${therapist_id}`);
  }
  if (addon_type_ids) {
    reqUrl = reqUrl.concat(`&addon_type_ids=${addon_type_ids}`);
  }

  try {
    const result = yield call(request, reqUrl, reqObj);
    const availableSlots = result.items;
    const notAvailableTillDate = result.notAvailableTillDate;
    yield put(fetchAvailableTherapistsSuccess(availableSlots));
    yield put(setNotAvailableTillDate(notAvailableTillDate));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchAvailableTherapistsFailure,
      'therapists fetch failed',
    );
  }
}

function* watchFetchAvailableTherapists() {
  yield takeLatest(FETCH_AVAILABLE_THERAPISTS, fetchAvailableTherapistsSaga);
}

export function* fetchUserPressureSaga() {
  const reqObj = buildRequestObj('GET', {}, true);
  const reqUrl = `${Config.apiBasev2}/user/preferences`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(fetchUserPressureSuccess(result.items));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchUserPressureFailure,
      'Could not fetch User Pressure',
    );
  }
}
function* watchFetchUserPressure() {
  yield takeLatest(FETCH_USER_PRESSURE, fetchUserPressureSaga);
}

export function* updateUserPressureSaga(action) {
  // eslint-disable-next-line
  const { customer_pressure, bun_in_the_oven, is_first_trimester } = action;
  const reqObj = buildRequestObj(
    'POST',
    { preferences: { customer_pressure, bun_in_the_oven, is_first_trimester } },
    true,
  );
  const reqUrl = `${Config.apiBasev2}/user/preferences`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(updateUserPressureSuccess(result.items));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      updateUserPressureFailure,
      'Could not Update User Pressure',
    );
  }
}

function* watchUpdateUserPressureSaga() {
  yield takeLatest(UPDATE_USER_PRESSURE, updateUserPressureSaga);
}

export function* checkGiftCodeSaga(action) {
  // To-Do: Create a util function to get data from local storage
  // values from local storage
  const {
    amount,
    slot,
    coupon,
    purchaseType,
    location_id,
    start_date_time,
    booker_service_id,
    qty,
    membership_length,
    codeKind,
    gift_card_code: companionGiftCardCode,
    promo_code: companionPromoCode,
  } = action;

  const userIdRaw = localStorage.getItem('userId');
  const user_id =
    userIdRaw && Number(userIdRaw) > 0 ? Number(userIdRaw) : undefined;
  const persistedState = loadState();
  const netAmount =
    amount ||
    persistedState.appointment.appointmentNetValue ||
    persistedState.gift.netTotal;

  const promoCodeReqObject = omitBy(
    buildCouponValidateRequest({
      coupon,
      codeKind,
      companionGiftCardCode,
      companionPromoCode,
      product: purchaseType,
      location: location_id || 1,
      start_at: slot,
      user_id,
      amount: netAmount,
      booker_service_id,
    }),
    val => val == null || val === '',
  );

  const reqObj = buildRequestObj(
    'POST',
    promoCodeReqObject,
    !!localStorage.getItem('userToken'),
  );
  const reqUrl = `${Config.apiBasev2}/coupon/validate`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    if (
      codeKind &&
      !couponValidateResultMatchesField({ ...result, code: coupon }, codeKind)
    ) {
      yield put(
        checkGiftCodeFailure(wrongFieldMessageForCodeKind(codeKind), codeKind),
      );
      return;
    }
    yield put(
      checkGiftCodeSuccess({
        ...result,
        code: coupon,
        codeKind: codeKind || null,
      }),
    );
  } catch (error) {
    yield yieldErrorHandling(
      error,
      err => checkGiftCodeFailure(err, codeKind),
      'gift code check failed',
    );
  }
}
function* watchCheckGiftCode() {
  yield takeLatest(CHECK_GIFT_CODE, checkGiftCodeSaga);
}

export function* createUserAppointmentSaga(action) {
  const { appointmentObj } = action;
  const user_id = localStorage.getItem('userId');
  const appointmentState = yield select(selectAppointmentDomain);
  const attribution = appointmentState.get('bookingAttribution');
  const attributionJS =
    attribution && typeof attribution.toJS === 'function'
      ? attribution.toJS()
      : attribution || {};

  const appointmentReqObj = buildRequestObj(
    'POST',
    {
      ...appointmentObj,
      ...buildConfirmAttributionPayload(attributionJS),
    },
    true,
  );
  const appointmentReqUrl = `${
    Config.apiBasev2
  }/user/${user_id}/appointment/confirm`;

  const timeout = 180000;

  try {
    localStorage.removeItem('referral_link');

    const { response, timeoutError } = yield race({
      response: call(request, appointmentReqUrl, appointmentReqObj),
      timeoutError: delay(timeout),
    });

    if (timeoutError) {
      throw new Error('Request timed out');
    }

    const appointmentId = response.id;
    const amount = response.non_member_price;
    Grin = window.Grin || (window.Grin = []);
    Grin.push(['conversion', amount, { order_number: appointmentId }]);

    yield put(createUserAppointmentSuccess({ success: true, items: response }));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      createUserAppointmentFailure,
      'User appointment creation failed',
    );
  }
}

function* watchCreateUserAppointment() {
  yield takeLatest(CREATE_USER_APPOINTMENT, createUserAppointmentSaga);
}

export function* updateAppointmentTextSaga(action) {
  // eslint-disable-next-line
  const { notificationObject } = action;
  const reqObj = buildRequestObj(
    'POST',
    {
      ...notificationObject,
    },
    true,
  );
  const reqUrl = `${Config.apiBasev2}/appointment/text`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(updateAppointmentTextSuccess(result));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      updateAppointmentTextFailure,
      'user appointment text notifications failed',
    );
  }
}

function* watchUpdateAppointmentTextSaga() {
  yield takeLatest(UPDATE_APPOINTMENT_TEXT, updateAppointmentTextSaga);
}

export function* fetchUserCompletedAppointmentsSaga(action) {
  const reqObj = buildRequestObj('GET', {}, true);
  const reqUrl = `${
    Config.apiBasev2
  }/appointments?status=completed&order_by=desc`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(fetchUserCompletedAppointmentsSuccess(result.items));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchUserCompletedAppointmentsFailure,
      'user appointments fetch failed',
    );
  }
}

function* watchFetchUserCompletedAppointments() {
  yield takeLatest(
    FETCH_USER_COMPLETED_APPOINTMENTS,
    fetchUserCompletedAppointmentsSaga,
  );
}

// Fetch appointments by status
export function* fetchAppointmentsSaga(action) {
  const reqObj = buildRequestObj('GET', {}, true);
  const reqUrl = `${Config.apiBasev2}/appointments?status=upcoming`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(
      fetchAppointmentsSuccess(
        sortBy(result.items, appointment => dayjs(appointment.start_date_time)),
      ),
    );
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchAppointmentsFailure,
      'user completed appointments fetch failed',
    );
  }
}
function* watchFetchAppointments() {
  yield takeLatest(FETCH_APPOINTMENTS, fetchAppointmentsSaga);
}

// get status
export function* fetchStatusSaga(action) {
  const { impersonateUser, statusFlowHint } = action;
  const reqObj = buildRequestObj('GET', {}, true, impersonateUser);
  let reqUrl = `${Config.apiBasev2}/status`;
  if (statusFlowHint === 'membership') {
    reqUrl = `${Config.apiBasev2}/status?flow=${encodeURIComponent(
      'membership-flow',
    )}`;
  } else if (statusFlowHint === 'gift') {
    reqUrl = `${Config.apiBasev2}/status?flow=${encodeURIComponent(
      'gift-flow',
    )}`;
  } else if (statusFlowHint === 'default') {
    // Impersonation booking: do not use stale isBuyingMembership / isBuyingGift from localStorage
    reqUrl = `${Config.apiBasev2}/status`;
  } else {
    if (
      window.localStorage.getItem('isBuyingMembership') ||
      window.localStorage.getItem('isBuyingFoundingMembership')
    ) {
      reqUrl = `${Config.apiBasev2}/status?flow=${encodeURIComponent(
        'membership-flow',
      )}`;
    }
    if (window.localStorage.getItem('isBuyingGift')) {
      reqUrl = `${Config.apiBasev2}/status?flow=${encodeURIComponent(
        'gift-flow',
      )}`;
    }
  }
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(fetchStatusSuccess(result));
    // const Result_sentry = JSON.stringify(result);
    // Sentry.captureMessage(Result_sentry);
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchStatusFailure,
      'user status fetch failed',
    );
  }
}

function* watchFetchStatus() {
  yield takeLatest(FETCH_STATUS, fetchStatusSaga);
}

export function* cancelUserAppointmentSaga(action) {
  const { appointment_id } = action;
  const reqObj = buildRequestObj('PUT', {}, true);
  const reqUrl = `${Config.apiBasev2}/appointment/${appointment_id}/cancel`;

  try {
    yield call(request, reqUrl, reqObj);
    yield put(cancelUserAppointmentSuccess({ success: true }));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      cancelUserAppointmentFailure,
      'user appointment cancellation failed',
    );
  }
}

function* watchCancelUserAppointment() {
  yield takeLatest(CANCEL_USER_APPOINTMENT, cancelUserAppointmentSaga);
}

export function* retryAppointmentChargeSaga(action) {
  const { spabooker_id } = action;
  const reqObj = buildRequestObj('POST', { spabooker_id }, true);
  const reqUrl = `${Config.apiBasev2}/appointment/retry`;
  try {
    const response = yield call(request, reqUrl, reqObj);
    const chargeResponse = response.appointments && response.appointments[0];
    const result = { ...chargeResponse, success: true };
    yield put(retryAppointmentChargeSuccess(result));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      retryAppointmentChargeFailure,
      'appointment charge retry',
    );
  }
}

function* watchRetryAppointmentCharge() {
  yield takeLatest(RETRY_APPOINTMENT_CHARGE, retryAppointmentChargeSaga);
}

export function* reserveAppointmentSaga(action) {
  const { appointmentObj } = action;
  const id = localStorage.getItem('userId');
  const {
    location_id,
    service_id,
    therapist_id,
    slot_time,
    pressure,
    similar_therapist,
  } = appointmentObj;
  const reqObj = buildRequestObj(
    'POST',
    {
      location_id,
      service_id,
      therapist_id,
      slot_time,
    },
    true,
  );
  const reqUrl = `${Config.apiBasev2}/user/${id}/appointment/reserve`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    const { bookingID } = result;
    const creatObj = {
      booking_id: bookingID,
      pressure,
      similar_therapist,
    };
    yield createUserAppointmentSaga(creatObj);
  } catch (error) {
    yield yieldErrorHandling(
      error,
      createUserAppointmentFailure,
      'user appointment reservation failed',
    );
  }
}

function* watchReserveAppointmentSaga() {
  yield takeLatest(RESERVE_APPOINTMENT_SAGA, reserveAppointmentSaga);
}

export function* applyReferralTrack() {
  const referral_link = localStorage.getItem('referral_link');
  const reqObj = buildRequestObj('POST', { referral_link }, true);
  try {
    const reqUrl = `${Config.apiBasev2}/user/referral/validate`;
    const result = yield call(request, reqUrl, reqObj);
    yield put(applyReferralSuccess(result));
  } catch (error) {
    // console.log(`error!!!`, error);
  }
}

function* watchApplyReferral() {
  yield takeLatest(APPLY_REFERRAL, applyReferralTrack);
}

export function* fetchNearestLocationSaga(action) {
  const { ipAddress, latLng, isBuyingRegularMembershipFlow } = action;
  const reqObj = buildRequestObj('GET', {}, false);
  let reqUrl = `${Config.apiBasev2}/nearest-location`;
  if (!isEmpty(latLng)) {
    const { lat, lng } = latLng;
    reqUrl =
      lat && lng
        ? `${Config.apiBasev2}/nearest-location?lat=${lat}&long=${lng}`
        : `${Config.apiBasev2}/nearest-location`;
  } else if (ipAddress) {
    reqUrl = !isEmpty(ipAddress)
      ? `${Config.apiBasev2}/nearest-location?ipAddress=${ipAddress}`
      : `${Config.apiBasev2}/nearest-location`;
  }
  try {
    const result = yield call(request, reqUrl, reqObj);
    const nearstLocation = result;
    // Filter out Somerville and Watertown locations (case-insensitive)
    const filteredItems = (nearstLocation.items || []).filter(
      loc => loc.launch_status === null && loc.is_active === 1,
    );
    const filteredResult = {
      ...nearstLocation,
      items: filteredItems,
      count: filteredItems.length,
    };
    if (isBuyingRegularMembershipFlow) {
      const activeLocations = filteredItems.filter((loc, index) => {
        if (loc.launch_status === null && loc.is_active === 1) {
          loc.serialNo = index + 1;
          return true;
        }
        return false;
      });
      activeLocations.forEach((loc, index) => {
        loc.serialNo = index + 1;
      });
      yield put(
        fetchNearestLocationSuccess({
          items: activeLocations,
          length: activeLocations.length,
        }),
      );
    } else {
      let indx = 0;
      filteredItems.filter(location => {
        if (location.launch_status === null && location.is_active === 1) {
          return location;
        }
      }).map(location => {
        location.serialNo = indx += 1;
        return location;
      });
      yield put(fetchNearestLocationSuccess(filteredResult));
    }
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchNearestLocationFailure,
      'fetch nearest location failed',
    );
  }
}

function* watchNearestLocation() {
  yield takeLatest(FETCH_NEAREST_LOCATION, fetchNearestLocationSaga);
}

export function* getIpAddress() {
  const reqObj = buildRequestObj('GET', {}, false);
  const reqUrl = 'https://api.ipify.org/?format=json';
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(getIpAddressSuccess(result));
  } catch (error) {
    yield yieldErrorHandling(error, getIpAddressFailure, 'Failed to fetch IP');
  }
}

function* watchGetIpAddress() {
  yield takeLatest(GET_IP_ADDRESS, getIpAddress);
}

export function* getLiabilityWaiverSaga(action) {
  const reqObj = buildRequestObj('GET', {}, true);
  const reqUrl = `${Config.apiBasev2}/liability_waiver`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(getLiabilityWaiverSuccess(result));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      getLiabilityWaiverFailure,
      'user liability waiver failed',
    );
  }
}

function* watchGetLiabilityWaiver() {
  yield takeLatest(GET_LIABILITY_WAIVER, getLiabilityWaiverSaga);
}

export function* sendSignatureSaga(action) {
  const { imageString } = action;
  
  // Validate imageString before sending
  if (!imageString || typeof imageString !== 'string') {
    console.error('Invalid imageString provided to sendSignatureSaga');
    yield put(sendSignatureFailure('Invalid signature data. Please try signing again.'));
    return;
  }

  // Check if imageString is too large (safety check)
  if (imageString.length > 10 * 1024 * 1024) { // 10MB limit
    console.error('Image string too large:', imageString.length);
    yield put(sendSignatureFailure('Signature image is too large. Please try signing again.'));
    return;
  }

  const reqObj = buildRequestObj('POST', { imageString }, true);
  const reqUrl = `${Config.apiBasev2}/liability_waiver`;
  
  // Retry logic for transient errors
  const maxRetries = 2;
  let lastError = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      const result = yield call(request, reqUrl, reqObj);
      yield put(sendSignatureSuccess(result));
      return; // Success, exit retry loop
    } catch (error) {
      lastError = error;
      
      // Enhanced error logging
      console.error(`Error submitting waiver signature (attempt ${attempt + 1}/${maxRetries + 1}):`, {
        status: error.status,
        message: error.message,
        data: error.data,
        imageSize: imageString ? imageString.length : 0,
      });

      // Only retry on transient errors (5xx, 408, 504, network errors)
      const isTransientError = 
        !error.status || // Network error (no status)
        error.status === 500 || 
        error.status === 502 || 
        error.status === 503 || 
        error.status === 504 || 
        error.status === 408;

      if (isTransientError && attempt < maxRetries) {
        // Wait before retrying (exponential backoff: 1s, 2s)
        const waitTime = 1000 * (attempt + 1);
        console.log(`Retrying waiver submission in ${waitTime}ms...`);
        yield delay(waitTime);
        continue; // Retry
      }

      // Not a transient error, or max retries reached
      break;
    }
  }

  // All retries exhausted or non-retryable error
  // Provide more specific error messages based on status code
  let errorMessage = 'Failed to submit signature. Please try again.';
  if (lastError.status === 413 || lastError.status === 400) {
    errorMessage = 'Signature image is too large or invalid. Please try signing again.';
  } else if (lastError.status === 401 || lastError.status === 403) {
    errorMessage = 'Authentication failed. Please refresh the page and try again.';
  } else if (lastError.status === 500 || lastError.status === 502 || lastError.status === 503) {
    errorMessage = 'Server error occurred. Please try again in a moment.';
  } else if (lastError.status === 408 || lastError.status === 504) {
    errorMessage = 'Request timed out. Please check your connection and try again.';
  } else if (!lastError.status) {
    errorMessage = 'Network error. Please check your connection and try again.';
  }

  yield yieldErrorHandling(
    lastError,
    sendSignatureFailure,
    errorMessage,
  );
}

function* watchSendSignature() {
  yield takeLatest(SEND_SIGNATURE, sendSignatureSaga);
}

export function* getSuggestedTipAmount(action) {
  const appointmentId = action.payload && action.payload.appointmentId;
  if (!appointmentId) return;
  const reqObj = buildRequestObj('GET', {}, true);
  const reqUrl = `${Config.apiBasev2}/appointment/${appointmentId}/suggested-tip-amounts`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(getSuggestedTipAmountSuccess(result));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      getSuggestedTipAmountSuccess,
      'Failed to get user tip amount!',
    );
  }
}

function* watchGetSuggestedTipAmount() {
  yield takeLatest(SUGGESTED_TIP_AMOUNT, getSuggestedTipAmount);
}

export function* sendRequiredGovtInfo(values) {
  const { first_name, last_name, state, city, address, zip } = values;
  const payload = {
    legal_first_name: first_name,
    legal_last_name: last_name,
    address: address,
    city: city,
    state: state,
    zipcode: String(zip),
  };
  const reqObj = buildRequestObj('POST', { ...payload }, true);
  try {
    const reqUrl = `${Config.apiBasev2}/user/legal-info`;
    const result = yield call(request, reqUrl, reqObj);
    yield put(sendRequiredGovtInfoSuccess(result));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      sendRequiredGovtInfoFailure,
      'send Required Govt Info failed',
    );
  }
}

function* watchSendRequiredGovtInfo() {
  yield takeLatest(SEND_REQUIRED_GOVT_INFO, sendRequiredGovtInfo);
}

export default {
  watchFetchLocations,
  watchFetchLocationServices,
  watchFetchLocationMoreInfo,
  watchFetchMassageTypes,
  watchFetchAvailableTherapists,
  watchCheckGiftCode,
  watchCreateUserAppointment,
  watchFetchUserPressure,
  watchUpdateUserPressureSaga,
  watchUpdateAppointmentTextSaga,
  watchFetchUserCompletedAppointments,
  watchFetchAppointments,
  watchCancelUserAppointment,
  watchRetryAppointmentCharge,
  watchReserveAppointmentSaga,
  watchApplyReferral,
  watchFetchStatus,
  watchNearestLocation,
  watchGetLiabilityWaiver,
  watchSendSignature,
  watchGetSuggestedTipAmount,
  watchGetIpAddress,
  watchSendRequiredGovtInfo,
  watchFetchServicesAddons,
  watchFetchUserServiceAddons,
  watchUpdateUserServiceAddonsSaga
};
